import React, { useEffect } from 'react';
import { format, parse } from 'date-fns';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Tooltip } from '@mui/material';

import OnDeleteIcon from '../../icons/OnDeleteIcon';
import { loadBaseAppointment } from '../../store/calendarService';
import { downloadParticipantDocumentById } from '../../store/documentService';
import { formatTime } from '../../utils/dateFunctions';
import { getNameFromId } from '../../utils/directusFunctions';
import { removeHtmlTags } from '../../utils/removeHtmlTags';
import { hasRole, QUALITY, SUPERUSER } from '../../utils/userRoles';
import Button from '../formElements/Button';

import TimelineEventHistoryManagement from './timelineEventHistory/TimelineEventHistoryManagement';
import TimelineRecruitmentEventHistoryManagement from './timelineRecruitmentEventHistory/TimelineRecruitmentEventHistoryManagement';

import timelineClasses from './timelineStyles/timeline.module.css';

const TimelineView = ({ row, viewAppointmentRoles, onDelete }) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const acceptedDeleteRoles = [QUALITY, SUPERUSER];
    const {
        attachments,
        businessName,
        details,
        eventDate,
        emailAddress,
        notes,
        eventHistory,
        eventStatus,
        eventStatusId,
        eventTime,
        eventType,
        history,
        itemTypeId,
        location,
        loggedDate,
        loggedTime,
        recipient,
        targetItemId,
        subject,
        userId,
        vacancyName,
        vacancyStage
    } = row;
    const fromEmailAddress = row.communicationEvent?.fromEmailAddress;
    const date = parse(eventDate, 'yyyy-MM-dd', new Date());
    let dateLogged;
    loggedDate ? (dateLogged = parse(loggedDate, 'yyyy-MM-dd', new Date())) : (dateLogged = '');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const users = useSelector((state) => state.entities.userService.users);
    const appointmentTypeDetails = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const attendanceDetails = useSelector(
        (state) => state.entities.directusService.attendanceDetails
    );
    const timelineTypeDetails = useSelector(
        (state) => state.entities.directusService.timelineTypeDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );

    const baseAppointment = useSelector((state) => state.entities.calendarService.baseAppointment);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (successMessage === `Base appointment has been loaded`) onNavigate();
    }, [successMessage]);

    // HELPER FNS
    const onNavigate = () => {
        navigate('/appointments', {
            state: {
                action: 'view',
                appointment: baseAppointment,
                appointmentAttendanceId: baseAppointment.attendances.find(
                    (el) => el.participantId === currentParticipant.id
                )?.id,
                participantId: currentParticipant.id,
                returnPath: '/timeline'
            }
        });
    };

    // EVENT HANDLERS
    const onViewAppointment = () => {
        dispatch(loadBaseAppointment(targetItemId));
    };

    const onDownload = (e, attachment) => {
        e.preventDefault();
        if (attachment.deleted) return;
        dispatch(
            downloadParticipantDocumentById(
                currentParticipant.id,
                attachment.documentId,
                attachment.filename
            )
        );
    };

    // RENDER
    return (
        <div className={timelineClasses.timelineBorder}>
            <div className={timelineClasses.timelineItem}>
                <span className={timelineClasses.time}>{format(date, 'dd MMM yy')}</span>

                <div className={timelineClasses.details}>
                    <details>
                        <summary>
                            <div className={timelineClasses.summaryTop}>
                                {getNameFromId(timelineTypeDetails, itemTypeId) === 'Appointment' &&
                                eventType !== 'Communication' ? (
                                    <span>
                                        {`${getNameFromId(
                                            timelineTypeDetails,
                                            itemTypeId
                                        )} - ${eventType}`}
                                    </span>
                                ) : history &&
                                  eventType !== 'PARTICIPANT' &&
                                  eventType !== 'Communication' &&
                                  eventType !== 'Outbound Communication' &&
                                  getNameFromId(timelineTypeDetails, itemTypeId) !==
                                      'Course Module' ? (
                                    <span>{`${eventType} <${vacancyName} - ${businessName}>`}</span>
                                ) : eventType === 'Outbound Communication' ? (
                                    <span>
                                        {`${eventType} - ${getNameFromId(
                                            timelineTypeDetails,
                                            itemTypeId
                                        )}`}
                                    </span>
                                ) : eventHistory && eventType === 'PARTICIPANT' ? (
                                    <span>{eventType}</span>
                                ) : getNameFromId(timelineTypeDetails, itemTypeId) ===
                                      'Course Module' && eventType !== 'Communication' ? (
                                    <span>{`${getNameFromId(
                                        timelineTypeDetails,
                                        itemTypeId
                                    )} - ${eventType}`}</span>
                                ) : (
                                    <span>
                                        {`Manual - ${eventType} - ${getNameFromId(
                                            timelineTypeDetails,
                                            itemTypeId
                                        )}`}
                                    </span>
                                )}
                                <span>
                                    <sup>
                                        {history &&
                                        eventType !== 'PARTICIPANT' &&
                                        eventType !== 'Communication' &&
                                        getNameFromId(timelineTypeDetails, itemTypeId) !==
                                            'Appointment' &&
                                        eventType !== 'Outbound Communication' &&
                                        getNameFromId(timelineTypeDetails, itemTypeId) !==
                                            'Course Module' &&
                                        eventType !== 'Recruitment Activity'
                                            ? formatTime(eventTime)
                                            : eventTime.length === 3
                                              ? `0${eventTime.substring(
                                                    0,
                                                    1
                                                )}:${eventTime.substring(1)}`
                                              : `${eventTime.substring(
                                                    0,
                                                    2
                                                )}:${eventTime.substring(2)}`}
                                    </sup>
                                    {getNameFromId(timelineTypeDetails, itemTypeId) ===
                                    'Appointment' ? (
                                        eventStatus === 'Attended' ? (
                                            <CheckCircleIcon className={timelineClasses.green} />
                                        ) : eventStatus === 'Not known' ? (
                                            <ErrorIcon className={timelineClasses.blue} />
                                        ) : (
                                            <CancelIcon className={timelineClasses.red} />
                                        )
                                    ) : eventType !== 'PARTICIPANT' &&
                                      eventType !== 'Communication' &&
                                      eventType !== 'Outbound Communication' &&
                                      getNameFromId(timelineTypeDetails, itemTypeId) !==
                                          'Course Module' ? (
                                        vacancyStage === 'Accepted' ? (
                                            <CheckCircleIcon className={timelineClasses.green} />
                                        ) : vacancyStage === 'Rejected' ||
                                          vacancyStage === 'Withdrawn' ? (
                                            <CancelIcon className={timelineClasses.red} />
                                        ) : (
                                            <ErrorIcon className={timelineClasses.blue} />
                                        )
                                    ) : getNameFromId(timelineTypeDetails, itemTypeId) ===
                                      'Course Module' ? (
                                        getNameFromId(attendanceDetails, eventStatusId) ===
                                        'Attended' ? (
                                            <CheckCircleIcon className={timelineClasses.green} />
                                        ) : getNameFromId(attendanceDetails, eventStatusId) ===
                                          'Not known' ? (
                                            <ErrorIcon className={timelineClasses.blue} />
                                        ) : (
                                            <CancelIcon className={timelineClasses.red} />
                                        )
                                    ) : (
                                        <CheckCircleIcon className={timelineClasses.green} />
                                    )}
                                </span>
                            </div>
                            <div className={timelineClasses.summaryBottom}>
                                <sup className={timelineClasses.adviserName}>
                                    {`User: ${
                                        emailAddress
                                            ? emailAddress
                                            : users.find((el) => el.id === userId)
                                                  ?.loginEmailAddress || ''
                                    }
                                        `}
                                </sup>
                                {!!fromEmailAddress && (
                                    <sup className={timelineClasses.adviserName}>
                                        {`From email address: ${fromEmailAddress}`}
                                    </sup>
                                )}
                                <sup className={timelineClasses.adviserName}>
                                    {getNameFromId(timelineTypeDetails, itemTypeId) === 'Email Sent'
                                        ? `Recipient: ${recipient}`
                                        : getNameFromId(timelineTypeDetails, itemTypeId) ===
                                                'Course Module' ||
                                            getNameFromId(timelineTypeDetails, itemTypeId) ===
                                                'Appointment'
                                          ? `Location: ${location ? location : ''}`
                                          : ''}
                                </sup>

                                {(history && eventType === 'PARTICIPANT') ||
                                history ||
                                getNameFromId(timelineTypeDetails, itemTypeId) === 'Appointment' ? (
                                    ''
                                ) : (
                                    <sup>
                                        Logged: dateLogged ? {format(dateLogged, 'dd MMM yy')} : ``
                                        {loggedTime
                                            ? loggedTime.length === 3
                                                ? `0${loggedTime.substring(
                                                      0,
                                                      1
                                                  )}:${loggedTime.substring(1)}`
                                                : `${loggedTime.substring(
                                                      0,
                                                      2
                                                  )}:${loggedTime.substring(2)}`
                                            : ''}
                                    </sup>
                                )}

                                <span className={timelineClasses.manualEntry}>
                                    {row?.manuallyCreated && (
                                        <OnDeleteIcon
                                            onDelete={() => onDelete(row.id)}
                                            roles={roles}
                                            acceptedRoles={acceptedDeleteRoles}
                                            active={hasRole(acceptedDeleteRoles, roles)}
                                        />
                                    )}
                                </span>
                            </div>
                            <div></div>
                        </summary>
                        <div className={timelineClasses.dropdown}>
                            {appointmentTypeDetails.some((el) => el.name === eventType) ? (
                                <>
                                    <div className={timelineClasses.timelineAppointment}>
                                        <div>
                                            <strong>Attendance Status: </strong>
                                            {getNameFromId(attendanceDetails, eventStatusId)}
                                        </div>
                                        <Button
                                            id={'addTimelineFilterButton'}
                                            disabled={!hasRole(viewAppointmentRoles, roles)}
                                            content="View Appointment"
                                            type="button"
                                            onClick={onViewAppointment}
                                            icon={true}
                                        />
                                    </div>
                                </>
                            ) : eventHistory &&
                              eventType === 'PARTICIPANT' &&
                              itemTypeId.startsWith('00000000') ? (
                                <>
                                    <div>
                                        <strong>{eventType}</strong>
                                    </div>
                                    <span>
                                        <TimelineEventHistoryManagement
                                            eventHistory={eventHistory}
                                        />
                                    </span>
                                </>
                            ) : history && eventType === 'Recruitment Activity' ? (
                                <>
                                    <>
                                        <div>
                                            <strong>Notes: </strong>
                                            <p className={timelineClasses.rejection}>{notes}</p>
                                        </div>
                                    </>
                                    <div>
                                        <strong>{history[0].field}:</strong>
                                    </div>
                                    <div>
                                        <TimelineRecruitmentEventHistoryManagement
                                            eventHistory={history}
                                        />
                                    </div>
                                </>
                            ) : eventType === 'Outbound Communication' ? (
                                <>
                                    <div>
                                        <div className={timelineClasses.divOnly}>
                                            <strong>Subject: </strong>
                                            {getNameFromId(timelineTypeDetails, itemTypeId) ===
                                            'SMS Sent'
                                                ? subject === '-'
                                                    ? 'Ad Hoc SMS'
                                                    : `${subject}`
                                                : getNameFromId(timelineTypeDetails, itemTypeId) ===
                                                    'Email Sent'
                                                  ? subject === '-'
                                                      ? 'Ad Hoc Email'
                                                      : `${subject}`
                                                  : getNameFromId(
                                                          timelineTypeDetails,
                                                          itemTypeId
                                                      ) === 'Letter Generated'
                                                    ? subject === '-'
                                                        ? 'Ad Hoc Letter Generated'
                                                        : `${subject}`
                                                    : ''}{' '}
                                        </div>
                                        <div className={timelineClasses.divOnly}>
                                            <strong>Attachments: </strong>
                                            {attachments.length > 0
                                                ? attachments.map((el) => (
                                                      <div key={el.documentId}>
                                                          <Tooltip
                                                              disableHoverListener={el.deleted}
                                                              title={`Download ${el.filename}`}
                                                              placement="right">
                                                              <a
                                                                  className={`${
                                                                      el.deleted
                                                                          ? timelineClasses.attachmentLink
                                                                          : ''
                                                                  }`}
                                                                  data-testid="attachments_start"
                                                                  href="#"
                                                                  onClick={(e) => {
                                                                      onDownload(e, el);
                                                                  }}>
                                                                  {' '}
                                                                  {el.filename}
                                                              </a>
                                                          </Tooltip>
                                                      </div>
                                                  ))
                                                : 'None'}
                                        </div>
                                        <div>
                                            <strong>Notes:</strong>
                                            <p className={timelineClasses.appointment}>
                                                {removeHtmlTags(details)}
                                            </p>
                                        </div>
                                    </div>
                                </>
                            ) : getNameFromId(timelineTypeDetails, itemTypeId) ===
                              'Course Module' ? (
                                <>
                                    <div>
                                        <strong>Module Description:</strong>
                                        <p className={timelineClasses.appointment}>{notes}</p>
                                    </div>
                                    <div>
                                        <strong>Course Status: </strong>
                                        {`${getNameFromId(attendanceDetails, eventStatusId)}`}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <strong>Message: </strong>
                                        <p className={timelineClasses.appointment}>{notes}</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </details>
                </div>
            </div>
        </div>
    );
};

TimelineView.propTypes = {
    row: PropTypes.object.isRequired,
    viewAppointmentRoles: PropTypes.arrayOf(PropTypes.string),
    onDelete: PropTypes.func
};

export default TimelineView;
